// grant — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'grant' })` op (DSE2 §3.F2).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice F2 — GRANT / REVOKE UI.
//
// Per-engine support (matches DSE2 dialect.grant):
//   - mssql / postgres / mysql / oracle: supported.
//   - sqlite: rejected — sqlite has no auth model; connection-level access is
//     OS / file-permission driven.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, GrantDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface GrantArgs {
  readonly grant: GrantDef;
}

export function buildGrantEdit(args: GrantArgs): DdlEdit {
  return { op: 'grant', grant: args.grant };
}

export async function commitGrant(
  ctx: VqeDdlEditContext,
  args: GrantArgs,
): Promise<void> {
  const edit = buildGrantEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isGrantSupported(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getGrantUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return 'SQLite has no auth model — connection-level access is OS / file-permission driven.';
  }
  return '';
}

export function validateGrantForEngine(
  engine: SqlEngine,
  args: GrantArgs,
): string | null {
  if (!isGrantSupported(engine)) {
    return getGrantUnsupportedTooltip(engine);
  }
  const g = args.grant;
  const allShortcut = engine === 'postgres' && g.pgsqlAllPrivileges === true;
  if (!allShortcut && g.privileges.length === 0) {
    return 'At least one privilege is required (e.g. SELECT, INSERT, EXECUTE).';
  }
  if (g.object.kind !== 'system' && g.object.kind !== 'role') {
    if (!g.object.name || g.object.name.trim().length === 0) {
      return `GRANT ON ${g.object.kind.toUpperCase()} requires an object name.`;
    }
  }
  if (g.grantees.length === 0) {
    return 'At least one grantee is required.';
  }
  for (const p of g.grantees) {
    if (p.kind !== 'public' && (!p.name || p.name.trim().length === 0)) {
      return `Grantee of kind '${p.kind}' requires a name.`;
    }
  }
  if (engine === 'oracle' && g.withGrantOption && g.oracleWithAdminOption) {
    return 'Oracle: WITH GRANT OPTION and WITH ADMIN OPTION are mutually exclusive.';
  }
  if (engine !== 'oracle' && g.oracleWithAdminOption) {
    return `${engine} does not accept oracleWithAdminOption.`;
  }
  if (engine !== 'postgres'
    && (g.pgsqlAllPrivileges || g.pgsqlDefaultPrivileges !== undefined)) {
    return `${engine} does not accept postgres-only fields (pgsqlAllPrivileges / pgsqlDefaultPrivileges).`;
  }
  if (engine !== 'mssql' && g.mssqlGrantedBy !== undefined) {
    return `${engine} does not accept mssqlGrantedBy.`;
  }
  if (engine === 'oracle'
    && (g.object.kind === 'schema' || g.object.kind === 'database'
      || g.object.kind === 'tablespace')) {
    return `Oracle: schema / database / tablespace privileges are expressed via system privileges. Use 'system' object kind with names like 'CREATE TABLESPACE'.`;
  }
  if (engine !== 'oracle' && g.object.kind === 'system') {
    return `${engine} does not support 'system' object kind — system privileges are oracle-specific.`;
  }
  return null;
}
